import { Dispatch, SetStateAction, useEffect } from "react";

import { IntlProvider } from "react-intl-next";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useLocale } from "@/libraries/web-framework";
import { experiments, useExperiment } from "@/utils/experiment";

import { ANALYTICS_PREFIX } from "../../../../onboarding-wizard/common/constants";
import { Jfind1663CreateProjectTaskPollingProvider } from "../../../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-create-project-task-polling-context";
import { useJfind1663FetchInterceptor } from "../../../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-interceptor";
import { LeftHandSidePageInvite } from "../../../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/left-hand-side/left-hand-side";
import { StepContext, StepProps } from "../../../types";
import { Header } from "../../components/header";

export const Invite = <T extends StepContext>({
    context,
    setContext,
    nextStep
}: StepProps<T>) => {
    const { teamType, template } = context;
    const locale = useLocale();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const experiment = useExperiment(
        experiments.jfindOnboardingWizardInviteStep
    );

    const isEnrolledInInviteTreatment =
        experiment.type === "enrolled" && experiment.value === true;

    useEffect(() => {
        if (isEnrolledInInviteTreatment) {
            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: `${ANALYTICS_PREFIX}InviteStep`,
                attributes: {
                    teamType,
                    template
                }
            }).fire();
        }
    }, [createAnalyticsEvent, teamType, template, isEnrolledInInviteTreatment]);

    useEffect(() => {
        if (experiment.type === "enrolled") {
            experiment.fireExposure();
        }

        if (!isEnrolledInInviteTreatment) {
            nextStep();
        }
    }, [experiment, isEnrolledInInviteTreatment, nextStep]);

    // skip invite step if not in treatment
    if (!isEnrolledInInviteTreatment) {
        return null;
    }

    return (
        <IntlProvider locale={locale}>
            <Jfind1663CreateProjectTaskPollingProvider>
                <LeftHandSidePageInviteWrapper<T>
                    nextStep={nextStep}
                    context={context}
                    setContext={setContext}
                />
            </Jfind1663CreateProjectTaskPollingProvider>
        </IntlProvider>
    );
};

/**
 * Wrapper to ensure Jfind1663CreateProjectTaskPollingProvider context is accessible
 * @returns JSX.Element
 */
const LeftHandSidePageInviteWrapper = <T extends StepContext>({
    nextStep,
    context: { tenantId },
    setContext
}: {
    nextStep: () => void;
    context: StepContext;
    setContext: Dispatch<SetStateAction<T>>;
}) => {
    // setup fetch interceptor for invite component
    useJfind1663FetchInterceptor({ cloudId: tenantId });

    const onStepComplete = (delayedAction?: () => Promise<void>) => {
        // go to next step and do not wait for invite to complete
        nextStep();

        // store delayedAction in context to wait for project creation to complete in loading step
        if (delayedAction) {
            setContext((prevContext) => ({
                ...prevContext,
                promisesToResolve: prevContext.promisesToResolve.concat([
                    delayedAction
                ])
            }));
        }
    };

    return <LeftHandSidePageInvite onSubmit={onStepComplete} Header={Header} />;
};
